import { useState, useEffect, useCallback, useRef, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FolderOpen, Loader2, Pencil, Trash2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getProjects, createProject, updateProject, deleteProject, duplicateProject, type ProjectData } from '@/api/modules/sessions';
import { EmptyState } from '@/components/empty-state';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Select } from '@/components/ui/select';

// 彩色流星雨底图配置：--mc 流星色（hsl 分量变量），top/left 起始点，duration/delay 错峰
const METEORS: Array<{ color: string; top: string; left: string; duration: string; delay: string }> = [
  { color: 'var(--neon-cyan)', top: '-12%', left: '12%', duration: '6.5s', delay: '0.6s' },
  { color: 'var(--neon-purple)', top: '-8%', left: '34%', duration: '8s', delay: '2.4s' },
  { color: 'var(--neon-pink)', top: '-15%', left: '55%', duration: '7.2s', delay: '4.1s' },
  { color: 'var(--neon-blue)', top: '-6%', left: '72%', duration: '9s', delay: '1.2s' },
  { color: 'var(--neon-yellow)', top: '-10%', left: '86%', duration: '7.8s', delay: '5.6s' },
  { color: 'var(--neon-orange)', top: '-18%', left: '45%', duration: '10s', delay: '6.8s' },
];

// ============ CreateProjectDialog ============

interface CreateProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: { name: string; description: string }) => Promise<void>;
}

function CreateProjectDialog({ open, onClose, onCreate }: CreateProjectDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await onCreate({ name: name.trim(), description: description.trim() });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-card w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-neon-cyan to-neon-purple">
            <FolderOpen size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">新建游戏项目</h3>
            <p className="text-xs text-muted-foreground">填写项目信息，选择模板开始创作</p>
          </div>
        </div>

        {/* Project name */}
        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-medium text-foreground">
            项目标题 <span className="text-red-400">*</span>
          </label>
          <input
            autoFocus
            className="input-neon w-full px-3 py-2.5 text-sm"
            placeholder="给你的游戏起个名字，例如：太空跑酷、水果消消乐..."
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
          />
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-medium text-foreground">项目描述</label>
          <textarea
            className="input-neon w-full resize-none px-3 py-2.5 text-sm"
            placeholder="描述一下你想做的游戏玩法、风格、目标平台..."
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
          />
          <span className="mt-1 block text-[10px] text-muted-foreground">{description.length}/500</span>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 border-t border-border/50 pt-4">
          <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted">取消</button>
          <button
            onClick={handleSubmit}
            disabled={loading || !name.trim()}
            className="glow-btn flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium disabled:opacity-40"
          >
            <Plus size={14} />
            {loading ? '创建中...' : '创建项目'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ CreationCenter Page ============

export function CreationCenter(): JSX.Element {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [editingProject, setEditingProject] = useState<ProjectData | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const { confirm, dialog } = useConfirm();

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProjects();
      setProjects(res.data);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleCreate = async (data: { name: string; description: string }) => {
    try {
      const res = await createProject(data.name, data.description || undefined);
      setShowCreate(false);
      toast.success('项目创建成功');
      navigate(`/creation/${res.data.id}`);
    } catch {
      toast.error('创建失败，请确认后端服务已启动');
    }
  };

  const handleDelete = (id: string) => {
    confirm('删除项目', '确定要删除这个项目吗？此操作不可撤销。', () => {
      // 乐观更新：先立即从列表移除卡片，后台再执行删除，提升删除感知速度
      setProjects(prev => prev.filter(p => p.id !== id));
      deleteProject(id)
        .then(() => toast.success('已删除'))
        .catch(() => {
          toast.error('删除失败');
          fetchProjects(); // 回滚：重新拉取列表恢复被移除的卡片
        });
    });
  };

  const handleDuplicate = (id: string, name: string) => {
    confirm('复制项目', `确定要复制「${name}」吗？将创建一个包含全部内容的副本。`, () => {
      duplicateProject(id)
        .then(() => {
          toast.success('项目已复制');
          fetchProjects();
        })
        .catch(() => toast.error('复制失败，请重试'));
    });
  };

  // ── 编辑弹窗自动保存（防抖 800ms + 关闭时兑底保存）──
  const saveTimer = useRef<number | null>(null);
  const [autoSaved, setAutoSaved] = useState(false);
  useEffect(() => {
    if (!editingProject) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      const name = editForm.name.trim();
      if (!name) return;
      updateProject(editingProject.id, { name, description: editForm.description.trim() })
        .then(() => {
          setProjects(prev => prev.map(p => p.id === editingProject.id ? { ...p, name, description: editForm.description.trim() } : p));
          setAutoSaved(true);
          setTimeout(() => setAutoSaved(false), 1500);
        })
        .catch(() => toast.error('自动保存失败，请重试'));
    }, 800);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
  }, [editForm, editingProject]);

  const handleCloseEdit = async () => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    if (editingProject) {
      const name = editForm.name.trim();
      if (name && (name !== editingProject.name || editForm.description.trim() !== (editingProject.description ?? ''))) {
        try {
          await updateProject(editingProject.id, { name, description: editForm.description.trim() });
          setProjects(prev => prev.map(p => p.id === editingProject.id ? { ...p, name, description: editForm.description.trim() } : p));
        } catch { /* 静默失败，下次进入编辑可重试 */ }
      }
    }
    setEditingProject(null);
  };

  const filtered = projects.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name?.toLowerCase().includes(q) || p.id?.toLowerCase().includes(q);
    const matchStatus = !statusFilter || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="relative isolate flex flex-1 flex-col">
      {/* 页面背景已上移至 AppLayout 根层级（z-0，仅 /creation 路由）——页面内 fixed 全视口会盖住 z-10 的侧栏（2026-08-26 实测） */}
      {/* 彩色流星雨底图（isolate 层叠上下文内 z-[-1] 垫底，不影响交互） */}
      <div aria-hidden className="meteor-field z-[-1]">
        {METEORS.map((m, i) => (
          <i
            key={i}
            className="meteor"
            style={{ '--mc': m.color, top: m.top, left: m.left, animationDuration: m.duration, animationDelay: m.delay } as CSSProperties}
          />
        ))}
      </div>
      <div className="page-header">
        <h2 className="page-title" style={{ animation: 'none' }}>
          <FolderOpen size={18} className="shrink-0 text-neon-cyan" />
          创作中心
        </h2>
      </div>
      <div className="mb-4 flex items-center gap-2">
        <div className="relative w-[400px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className="input-neon w-full py-2 pl-8 pr-3 text-sm" placeholder="搜索..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select
          options={[
            { value: '', label: '全部状态' },
            { value: 'draft', label: '制作中' },
            { value: 'published', label: '已发布' },
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
          className="input-neon py-2 px-3 text-sm"
        />
        <button onClick={() => setShowCreate(true)} className="glow-btn flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium"><Plus size={15} />新建项目</button>
      </div>

      {/* Project list */}
      {loading ? (
        <div className="flex flex-1 flex-col items-center justify-start gap-3 pt-28">
          <Loader2 size={32} className="animate-spin text-neon-cyan" />
          <span className="text-xs text-muted-foreground">加载中...</span>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={search ? '未找到匹配项目' : '还没有项目'}
          description={search ? '试试其他关键词' : '点击"新建项目"开始你的第一个游戏创作'}
          action={!search ? { label: '新建项目', onClick: () => setShowCreate(true) } : undefined}
        />
      ) : (
        <div className="glass-card overflow-hidden hover:border-border/70 hover:shadow-none">
          {/* 表头 */}
          <div className="grid grid-cols-[minmax(0,1fr)_70px_70px_110px_190px_50px_150px_150px_110px] items-center gap-2 border-b border-border/30 bg-muted/20 px-4 py-2.5 text-[11px] font-medium text-muted-foreground">
            <span>项目</span>
            <span>状态</span>
            <span>试玩</span>
            <span>最佳版本</span>
            <span>最近试玩</span>
            <span>会话</span>
            <span>创建时间</span>
            <span>更新时间</span>
            <span className="text-right">操作</span>
          </div>
          <div className="divide-y divide-border/20">
            {filtered.map((p) => {
              const s = p._summary;
              // 所有时间列统一 YYYY/MM/DD HH:mm:ss（含年份与时分秒）
              const shortDate = (d?: string) => d ? new Date(d).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
              const fullDateTime = shortDate;
              return (
                <div
                  key={p.id}
                  className="grid cursor-pointer grid-cols-[minmax(0,1fr)_70px_70px_110px_190px_50px_150px_150px_110px] items-center gap-2 px-4 py-3 transition-colors hover:bg-primary/10 hover:shadow-[inset_2px_0_0_0_hsl(var(--primary))]"
                  onClick={() => navigate(`/creation/${p.id}`)}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-neon-cyan/20 to-neon-purple/20 text-neon-cyan">
                      <FolderOpen size={15} />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{p.name}</p>
                      {p.icon && <p className="truncate text-[11px] text-muted-foreground">{p.icon}</p>}
                    </div>
                  </div>
                  <span className="text-xs">
                    {p.status === 'published' ? (
                      <span className="rounded bg-neon-green/15 px-1.5 py-0.5 text-[10px] font-medium text-neon-green">已发布</span>
                    ) : (
                      <span className="rounded bg-muted/40 px-1.5 py-0.5 text-[10px] text-muted-foreground">制作中</span>
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground">{s?.playableCount ?? 0} 个试玩</span>
                  <span className="text-xs text-muted-foreground">
                    {s?.bestPlayable ? `最佳 V${s.bestPlayable.version} (${s.bestPlayable.score}分)` : '—'}
                  </span>
                  <span className="tech-mono text-xs text-muted-foreground">
                    {s?.latestPlayable ? `V${s.latestPlayable.version} · ${shortDate(s.latestPlayable.createdAt)}` : '—'}
                  </span>
                  <span className="text-xs text-muted-foreground">{s?.sessionCount ?? 0}</span>
                  <span className="tech-mono text-xs text-muted-foreground">{fullDateTime(p.createdAt)}</span>
                  <span className="tech-mono text-xs text-muted-foreground">{fullDateTime(p.updatedAt)}</span>
                  <div className="flex items-center justify-end gap-1"
                    onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleDuplicate(p.id, p.name)}
                      className="icon-btn rounded p-1.5" title="复制项目">
                      <Copy size={12} />
                    </button>
                    <button
                      onClick={() => { setEditingProject(p); setEditForm({ name: p.name, description: p.description || '' }); }}
                      className="icon-btn rounded p-1.5" title="重命名">
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => handleDelete(p.id)}
                      className="icon-btn danger rounded p-1.5" title="删除">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <CreateProjectDialog open={showCreate} onClose={() => setShowCreate(false)} onCreate={handleCreate} />
      {dialog}

      {/* Edit Project Dialog（自动保存：输入防抖 800ms 保存，关闭时兑底保存） */}
      {editingProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => handleCloseEdit()}>
          <div className="glass-card w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">编辑项目</h3>
              {autoSaved && (
                <span className="flex items-center gap-1 text-[11px] text-neon-green">
                  <Check size={12} />已自动保存
                </span>
              )}
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">名称</label>
                <input
                  autoFocus
                  className="input-neon w-full px-3 py-2 text-sm"
                  value={editForm.name}
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter') handleCloseEdit(); }}
                  maxLength={50}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">描述</label>
                <textarea
                  className="input-neon w-full resize-none px-3 py-2 text-sm"
                  rows={3}
                  value={editForm.description}
                  onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                  maxLength={500}
                />
                <span className="mt-1 block text-[10px] text-muted-foreground">{editForm.description.length}/500</span>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">输入后自动保存</span>
              <button onClick={() => handleCloseEdit()} className="rounded-lg border border-border px-4 py-2 text-sm">关闭</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
